/**
 * Projektstatus — en gemensam beräkning för alla roller och vyer.
 *
 * Logiken fanns tidigare i fyra kopior (OversiktSection, ProjektSection,
 * Dashboard och backendens myAssignedProjectsRoutes) och hade hunnit glida isär.
 * Framför allt saknade den variant som byggare, UE och projektör fick veta om
 * "Försenat" — deras kort visade bara "Pågående" långt efter slutdatum, medan
 * byggkonsulten såg "Pågående · Försenat (108 dgr över)" på samma projekt.
 *
 * Regler:
 * - Manuell completionStatus vinner alltid.
 * - Utan datum är projektet i Projektering.
 * - Efter slutdatum utan manuellt avslut är projektet FÖRSENAT, inte avslutat.
 *   Att markera ett projekt klart är ett aktivt beslut som byggkonsulten tar.
 *
 * Datumen läses från tidplanen i första hand — den speglar den publicerade
 * versionen — och faller tillbaka på projektets egna fält.
 */

export const getProjectStatus = (project) => {
  if (!project) {
    return { status: 'Projektering', message: '', color: 'bg-violet-100 text-violet-800' };
  }

  // Manuellt avslutat av byggkonsult
  if (project.completionStatus === 'Avslutat') {
    return { status: 'Avslutat', message: '', color: 'bg-green-100 text-green-800' };
  }
  if (project.completionStatus === 'Anmärkning/Tillägg') {
    return { status: 'Anmärkning/Tillägg', message: '', color: 'bg-amber-100 text-amber-800' };
  }

  const startDate = project.timeline?.startDate
    ? new Date(project.timeline.startDate)
    : (project.startDate ? new Date(project.startDate) : null);
  const endDate = project.timeline?.endDate
    ? new Date(project.timeline.endDate)
    : (project.endDate ? new Date(project.endDate) : null);

  if (!startDate || !endDate) {
    return { status: 'Projektering', message: '', color: 'bg-violet-100 text-violet-800' };
  }

  const now = new Date();

  if (now < startDate) {
    return {
      status: 'Planerad',
      message: startDate.toLocaleDateString('sv-SE'),
      color: 'bg-blue-100 text-blue-800'
    };
  }

  if (now > endDate) {
    const daysOverdue = Math.ceil((now - endDate) / (1000 * 60 * 60 * 24));
    return {
      status: 'Pågående',
      subStatus: 'Försenat',
      message: `${daysOverdue} dgr över`,
      daysOverdue,
      color: 'bg-orange-100 text-orange-800',
      overdue: true
    };
  }

  const daysUntilEnd = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
  return {
    status: 'Pågående',
    message: `${daysUntilEnd} dgr kvar`,
    daysUntilEnd,
    color: 'bg-leadme-orange-light text-leadme-orange-superdark'
  };
};

/**
 * Färdig etikett: "Pågående · Försenat (108 dgr över)" eller "Pågående (12 dgr kvar)".
 */
export const getProjectStatusLabel = (project) => {
  const s = getProjectStatus(project);
  const bas = s.subStatus ? `${s.status} · ${s.subStatus}` : s.status;
  return s.message ? `${bas} (${s.message})` : bas;
};

/** Bara statusnamnet, för kod som jämför mot strängar */
export const getProjectStatusName = (project) => getProjectStatus(project).status;
